import React, { useEffect, useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import { Button, Tab } from "@verily-src/react-design-system";
import { TabPanel, tabA11yProps } from "components/TabPanel";
import { Role } from "generated/studyauth/studyauth_pb";
import useStudyUsersDataLoader from "./StudyUsersDataLoader";
import StudyUsers from "./StudyUsers";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  hasAnyRole,
  hasRoleInStudy,
  selectCurrentStudyRoles,
} from "core/UserConfigSlice";
import StudySiteUsers from "./StudySiteUsers";
import useStudySitesDataLoader from "studysite/StudySitesDataLoader";
import SingleStudySiteUsers from "./SingleStudySiteUsers";
import { usePageTitle } from "components/PageTitle";
import Loading from "components/Loading";
import { useNavigate } from "react-router-dom";
import {
  setParticipantChangeLogState,
  WhichChangeLogsTable,
} from "common/ChangeLogsStateSlice";
import { AppRoutes } from "core/AppRoutes";
import { FocusOn, resetViewState } from "common/CommonViewState";
import { focusOnRef } from "common/Accessibility";

enum TabIndex {
  VerilyStudyAdmin = 0,
  VerilyCDM = 1,
  SponsorStudyAdmin = 2,
  CRC = 3,
}

const StudyUsersHome: React.FC = () => {
  const currentRegistryId = useAppSelector(
    (state) => state.userConfig.selectedRegistryId
  );

  const selectedRoles = useAppSelector((state) =>
    selectCurrentStudyRoles(state.userConfig)
  );

  // Get login user's study site.
  // Note that CRCs can only manage one site per study.
  const studySiteIdFilter = useMemo(() => {
    for (let userRole of selectedRoles) {
      if (userRole.role === Role.CRC) {
        return userRole.studySiteId;
      }
    }

    return "";
  }, [selectedRoles]);

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const {
    isLoading: isLoadingUsers,
    hasError: hasErrorLoadingUsers,
    users,
    userRolesMap,
    loadUsers,
  } = useStudyUsersDataLoader();

  const {
    isLoading: isLoadingSites,
    hasError: hasErrorLoadingSites,
    studySites,
    loadStudySites,
  } = useStudySitesDataLoader();

  const hasLoadingError = useMemo(
    () => hasErrorLoadingUsers || hasErrorLoadingSites,
    [hasErrorLoadingSites, hasErrorLoadingUsers]
  );

  // Set page title.
  usePageTitle("Manage users");

  useEffect(() => {
    loadUsers();
    loadStudySites(currentRegistryId);
  }, [currentRegistryId, loadStudySites, loadUsers]);

  // Set initial tab selection based on user roles.
  useEffect(() => {
    if (hasAnyRole(selectedRoles, Role.SPONSOR_OPS, Role.CDM)) {
      setSelectedTabIndex(TabIndex.VerilyStudyAdmin);
    } else if (hasAnyRole(selectedRoles, Role.COMPLIANCE_ADMIN)) {
      setSelectedTabIndex(TabIndex.SponsorStudyAdmin);
    } else if (hasAnyRole(selectedRoles, Role.CRC)) {
      setSelectedTabIndex(TabIndex.CRC);
    }
  }, [selectedRoles]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const userRoles = useAppSelector((state) => state.userConfig.userRoles);
  var whichChangeLogsTable = WhichChangeLogsTable.USERS_CDM_OR_SPONSOR_OPS_VIEW;
  if (hasAnyRole(userRoles, Role.COMPLIANCE_ADMIN)) {
    whichChangeLogsTable = WhichChangeLogsTable.USERS_COMPLIANCE_ADMIN_VIEW;
  }
  const viewState = useAppSelector((state) => state.viewState);
  const dataChangeLogsButtonRef = React.useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (viewState.focusTarget === FocusOn.ChangeLogs) {
      focusOnRef(dataChangeLogsButtonRef);
      dispatch(resetViewState());
    }
  }, [viewState, dispatch, viewState.focusTarget]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "24px 40px 24px 40px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <Typography component="h1" variant="display5">
            Manage users
          </Typography>
          {hasAnyRole(userRoles, Role.COMPLIANCE_ADMIN, Role.SPONSOR_OPS) && (
            <Button
              variant="outlined"
              label="Data change logs"
              ref={dataChangeLogsButtonRef}
              onClick={() => {
                const hasCrcRole = hasAnyRole(selectedRoles, Role.CRC);
                dispatch(
                  setParticipantChangeLogState({
                    whichChangeLogsTable: whichChangeLogsTable,
                    returnRoute: hasCrcRole
                      ? AppRoutes.SITE_USERS
                      : AppRoutes.STUDY_USERS,
                    registryId: currentRegistryId,
                  })
                );
                navigate(
                  hasCrcRole
                    ? AppRoutes.SITE_USERS_CHANGE_LOGS
                    : AppRoutes.STUDY_USERS_CHANGE_LOGS
                );
              }}
              style={{ marginRight: "8px" }}
            />
          )}
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={selectedTabIndex} onChange={handleChange}>
              {hasAnyRole(selectedRoles, Role.SPONSOR_OPS, Role.CDM) && (
                <Tab
                  label="Operations"
                  value={TabIndex.VerilyStudyAdmin}
                  {...tabA11yProps(TabIndex.VerilyStudyAdmin)}
                />
              )}
              {hasAnyRole(selectedRoles, Role.SPONSOR_OPS, Role.CDM) && (
                <Tab
                  label="DMs"
                  value={TabIndex.VerilyCDM}
                  {...tabA11yProps(TabIndex.VerilyCDM)}
                />
              )}
              {hasAnyRole(
                selectedRoles,
                Role.SPONSOR_OPS,
                Role.CDM,
                Role.COMPLIANCE_ADMIN
              ) && (
                <Tab
                  label="Compliance Admins"
                  value={TabIndex.SponsorStudyAdmin}
                  {...tabA11yProps(TabIndex.SponsorStudyAdmin)}
                />
              )}
              <Tab
                label="Site Research Coordinators"
                value={TabIndex.CRC}
                {...tabA11yProps(TabIndex.CRC)}
              />
            </Tabs>
          </Box>
          {!hasLoadingError && (
            <>
              <TabPanel
                selectedTabIndex={selectedTabIndex}
                tabIndex={TabIndex.VerilyStudyAdmin}
              >
                <StudyUsers
                  isEditable={hasRoleInStudy(
                    selectedRoles,
                    currentRegistryId,
                    Role.CDM
                  )}
                  users={users}
                  userRolesMap={userRolesMap}
                  registryIdFilter={currentRegistryId}
                  userRoleFilter={Role.SPONSOR_OPS}
                  reloadData={() => {
                    loadUsers();
                  }}
                />
              </TabPanel>
              <TabPanel
                selectedTabIndex={selectedTabIndex}
                tabIndex={TabIndex.VerilyCDM}
              >
                <StudyUsers
                  isEditable={hasRoleInStudy(
                    selectedRoles,
                    currentRegistryId,
                    Role.CDM
                  )}
                  users={users}
                  userRolesMap={userRolesMap}
                  registryIdFilter={currentRegistryId}
                  userRoleFilter={Role.CDM}
                  reloadData={() => {
                    loadUsers();
                  }}
                />
              </TabPanel>
              <TabPanel
                selectedTabIndex={selectedTabIndex}
                tabIndex={TabIndex.SponsorStudyAdmin}
              >
                <StudyUsers
                  isEditable={hasRoleInStudy(
                    selectedRoles,
                    currentRegistryId,
                    Role.CDM
                  )}
                  users={users}
                  userRolesMap={userRolesMap}
                  registryIdFilter={currentRegistryId}
                  userRoleFilter={Role.COMPLIANCE_ADMIN}
                  reloadData={() => {
                    loadUsers();
                  }}
                />
              </TabPanel>
              <TabPanel
                selectedTabIndex={selectedTabIndex}
                tabIndex={TabIndex.CRC}
              >
                {hasAnyRole(
                  selectedRoles,
                  Role.SPONSOR_OPS,
                  Role.CDM,
                  Role.COMPLIANCE_ADMIN
                ) && (
                  <StudySiteUsers
                    isEditable={hasRoleInStudy(
                      selectedRoles,
                      currentRegistryId,
                      Role.CDM
                    )}
                    users={users}
                    userRolesMap={userRolesMap}
                    studySites={studySites}
                    registryIdFilter={currentRegistryId}
                    reloadData={() => {
                      loadUsers();
                    }}
                  />
                )}
                {!hasAnyRole(
                  selectedRoles,
                  Role.SPONSOR_OPS,
                  Role.CDM,
                  Role.COMPLIANCE_ADMIN
                ) && (
                  <SingleStudySiteUsers
                    users={users}
                    userRolesMap={userRolesMap}
                    registryIdFilter={currentRegistryId}
                    studySiteIdFilter={studySiteIdFilter}
                  />
                )}
              </TabPanel>
            </>
          )}
          {hasLoadingError && (
            <Alert
              severity="error"
              sx={{ paddingLeft: "16px", marginTop: "8px" }}
            >
              Failed to load users
            </Alert>
          )}
          {(isLoadingUsers || isLoadingSites) && <Loading />}
        </Box>
      </Box>
    </>
  );
};

export default StudyUsersHome;
